// frontend/src/components/CustomTimeline.js

import React from 'react';
import {
    Box,
    Typography,
    Avatar,
    Paper,
    styled
} from '@mui/material';

const TimelineContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: '20px',
        top: 0,
        bottom: 0,
        width: '2px',
        backgroundColor: theme.palette.divider,
    },
}));

const TimelineItemContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    minHeight: '70px',
    '&:last-child::before': {
        content: '""',
        position: 'absolute',
        left: '19px',
        top: '50px',
        bottom: 0,
        width: '2px',
        backgroundColor: theme.palette.background.default,
    },
}));

const TimelineDotContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    minWidth: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
}));

const TimelineContentContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(2),
}));

const TimelineOppositeContainer = styled(Box)(({ theme }) => ({
    minWidth: '120px',
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1),
    textAlign: 'right',
}));

// Componente Timeline principal
export const CustomTimeline = ({ children, sx = {}, ...props }) => {
    return (
        <TimelineContainer sx={sx} {...props}>
            {children}
        </TimelineContainer>
    );
};

// Componente TimelineItem
export const CustomTimelineItem = ({ children, ...props }) => {
    return (
        <TimelineItemContainer {...props}>
            {children}
        </TimelineItemContainer>
    );
};

// Componente TimelineOppositeContent
export const CustomTimelineOppositeContent = ({
    children,
    color = "textSecondary",
    sx = {},
    ...props
}) => {
    return (
        <TimelineOppositeContainer sx={sx} {...props}>
            <Typography variant="caption" color={color}>
                {children}
            </Typography>
        </TimelineOppositeContainer>
    );
};

// Componente TimelineSeparator
export const CustomTimelineSeparator = ({ children, ...props }) => {
    return (
        <TimelineDotContainer {...props}>
            {children}
        </TimelineDotContainer>
    );
};

// Componente TimelineDot
export const CustomTimelineDot = ({
    children,
    color = "grey",
    variant = "circular",
    sx = {},
    ...props
}) => {
    const getColor = (colorProp) => {
        switch (colorProp) {
            case 'primary': return 'primary.main';
            case 'secondary': return 'secondary.main';
            case 'error': return 'error.main';
            case 'warning': return 'warning.main';
            case 'info': return 'info.main';
            case 'success': return 'success.main';
            default: return 'grey.400';
        }
    };

    return (
        <Avatar
            sx={{
                bgcolor: getColor(color),
                width: 40,
                height: 40,
                fontSize: '1rem',
                ...sx
            }}
            {...props}
        >
            {children}
        </Avatar>
    );
};

// Componente TimelineConnector (no es necesario ya que la línea se dibuja con CSS)
export const CustomTimelineConnector = ({ ...props }) => {
    return null;
};

// Componente TimelineContent
export const CustomTimelineContent = ({ children, sx = {}, ...props }) => {
    return (
        <TimelineContentContainer sx={sx} {...props}>
            {children}
        </TimelineContentContainer>
    );
};

// Exportaciones individuales para compatibilidad
export const Timeline = CustomTimeline;
export const TimelineItem = CustomTimelineItem;
export const TimelineOppositeContent = CustomTimelineOppositeContent;
export const TimelineSeparator = CustomTimelineSeparator;
export const TimelineDot = CustomTimelineDot;
export const TimelineConnector = CustomTimelineConnector;
export const TimelineContent = CustomTimelineContent;

// Exportar como default un objeto con todos los componentes
const CustomTimelineComponents = {
    Timeline: CustomTimeline,
    TimelineItem: CustomTimelineItem,
    TimelineOppositeContent: CustomTimelineOppositeContent,
    TimelineSeparator: CustomTimelineSeparator,
    TimelineDot: CustomTimelineDot,
    TimelineConnector: CustomTimelineConnector,
    TimelineContent: CustomTimelineContent,
};

export default CustomTimelineComponents;